'use client'

import NextLink from 'next/link'
import { Accreditation, Qualification, ServiceFilter } from '@prisma/client'
import React from 'react'
import { Brand, InstallerEntry, ServiceFilterEntry, findBrands, findInstallers, findServiceFilters } from './actions'
import { formatNumber } from '@/lib/formatters'
import { DEFAULT_FILTERS, DISTANCE_FILTER_OPTIONS, distanceFilterSchema, Filters, filtersToParams, mediaFilterSchema } from './types'
import { Coordinates } from '@/lib/cities'
import { z } from 'zod'
import { SERVICE_FILTER_LABELS } from '@/lib/serviceFilters'
import { useRouter, useSearchParams } from 'next/navigation'
import Icon from '@/lib/Icon'
import Button from '@/lib/tailwind/Button'
import Chip from '@/lib/tailwind/Chip'
import Dialog from '@/lib/tailwind/Dialog'
import Radio from '@/lib/tailwind/Radio'

export type Props = {
  coordinates: Coordinates
  accreditations: Accreditation[]
  qualifications: Qualification[]
  initialBrands: Brand[]
  initialServiceFilters: ServiceFilterEntry[]
  initialInstallers: InstallerEntry[]
  initialTotal: number
  initialFilters: Filters
}

export default function Installers({
  coordinates,
  accreditations,
  qualifications,
  initialBrands,
  initialServiceFilters,
  initialInstallers,
  initialTotal,
  initialFilters,
}: Props) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [installers, setInstallers] = React.useState<InstallerEntry[]>(initialInstallers)
  const [brands, setBrands] = React.useState<Brand[]>(initialBrands)
  const [serviceFilters, setServiceFilters] = React.useState<ServiceFilterEntry[]>(initialServiceFilters)
  const [filters, setFilters] = React.useState<Filters>(initialFilters)
  const [isLoading, setLoading] = React.useState<boolean>(false)
  const [loadingPage, setLoadingPage] = React.useState<boolean>(false)
  const [page, setPage] = React.useState<number>(1)
  const [total, setTotal] = React.useState<number>(initialTotal)

  async function handleSetFilters(filters: Filters): Promise<void> {
    const params = filtersToParams(filters)
    const city = searchParams.get('city')
    if (city != null) {
      params.set('city', city)
    }
    const searchId = searchParams.get('searchId')
    if (searchId != null) {
      params.set('searchId', searchId)
    }
    router.replace(`?${params}`)

    setLoading(true)
    setFilters(filters)

    const [total, installers] = await findInstallers(coordinates, filters)
    const brands = await findBrands(coordinates, filters)
    const serviceFilters = await findServiceFilters(coordinates, filters)
    setBrands(brands)
    setServiceFilters(serviceFilters)
    setInstallers(installers)
    setTotal(total)
    setLoading(false)
  }

  async function handleLoadMore(): Promise<void> {
    setLoadingPage(true)
    const [, moreInstallers] = await findInstallers(coordinates, filters, page)
    setInstallers(installers.concat(moreInstallers))
    setPage(page + 1)
    setLoadingPage(false)
  }

  return (
    <div className="flex flex-col gap-4 my-4 max-w-screen-lg m-auto">
      <h1 className="text-2xl self-center hidden md:block">
        Installers near you
      </h1>
      <MobileFilters
        filters={filters}
        setFilters={handleSetFilters}
        accreditations={accreditations}
        qualifications={qualifications}
        brands={brands}
        serviceFilters={serviceFilters}
        totalInstallers={total}
      />
      <div className="flex flex-row items-start gap-4">
        <DesktopFilters
          filters={filters}
          setFilters={handleSetFilters}
          accreditations={accreditations}
          qualifications={qualifications}
          serviceFilters={serviceFilters}
          brands={brands}
        />
        {isLoading
          ? (
            <p className="flex-[1]">Loading...</p>
            )
          : (
            <div className="flex flex-col flex-1 gap-0 md:gap-2 max-md:divide-y max-md:divide-solid max-md:divide-lightBlue">
              <p className="hidden md:block">
                {`${total} relevant installer${total === 1 ? '' : 's'} found`}
              </p>
              {installers.map(installer => (
                <Installer key={installer.id} installer={installer} />
              ))}
              {installers.length < total && (
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingPage}
                  className="mt-4 md:mt-2 mx-4"
                >
                  Show more installers
                </Button>
              )}
            </div>
            )}
      </div>
    </div>
  )
}

function DesktopFilters({
  filters,
  setFilters,
  accreditations,
  qualifications,
  brands,
  serviceFilters,
}: {
  filters: Filters
  setFilters: (filters: Filters) => void
  accreditations: Accreditation[]
  qualifications: Qualification[]
  brands: Brand[]
  serviceFilters: ServiceFilterEntry[]
}): React.ReactNode {
  function onClearFilters() {
    setFilters(DEFAULT_FILTERS)
  }

  return (
    <div className="hidden md:flex flex-col w-[288px] rounded-lg border-2 border-outline bg-white">
      <div className="flex flex-row gap-4 justify-between items-center px-4 py-1 border-b-2 border-b-outline">
        <h2>Filters</h2>
        <button onClick={onClearFilters} className="text-darkBlue">
          Clear filters
        </button>
      </div>
      <AllFilters
        filters={filters}
        setFilters={setFilters}
        accreditations={accreditations}
        qualifications={qualifications}
        brands={brands}
        serviceFilters={serviceFilters}
      />
    </div>
  )
}

function MobileFilters({
  filters,
  setFilters,
  accreditations,
  qualifications,
  brands,
  serviceFilters,
  totalInstallers,
}: {
  filters: Filters
  setFilters: (filters: Filters) => void
  accreditations: Accreditation[]
  qualifications: Qualification[]
  brands: Brand[]
  serviceFilters: ServiceFilterEntry[]
  totalInstallers: number
}): React.ReactNode {
  const [open, setOpen] = React.useState<boolean>(false)

  return (
    <>
      <div className="flex md:hidden flex-row items-start justify-between mx-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl">
            Installers near you
          </h1>
          <p>
            {`${totalInstallers} relevant installer${totalInstallers === 1 ? '' : 's'} found`}
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="px-4">
          <Icon icon="filter" height={20} width={20} />
          Filter
        </Button>
      </div>
      <Dialog
        onClose={() => setOpen(false)}
        title="Filters"
        action={{
          onClick: () => setOpen(false),
          label: 'Apply filters and show results',
        }}
        open={open}
      >
        <AllFilters
          filters={filters}
          setFilters={setFilters}
          accreditations={accreditations}
          qualifications={qualifications}
          brands={brands}
          serviceFilters={serviceFilters}
        />
      </Dialog>
    </>
  )
}

function AllFilters({
  filters,
  setFilters,
  accreditations,
  qualifications,
  brands,
  serviceFilters,
}: {
  filters: Filters
  setFilters: (filters: Filters) => void
  accreditations: Accreditation[]
  qualifications: Qualification[]
  brands: Brand[]
  serviceFilters: ServiceFilterEntry[]
}): React.ReactNode {
  const accreditationOptions: CheckboxOption[] = React.useMemo(() => {
    return accreditations.map(({ id, name }) => ({ id, label: name }))
  }, [accreditations])

  const qualificationOptions: CheckboxOption[] = React.useMemo(() => {
    return qualifications.map(({ id, name }) => ({ id, label: name }))
  }, [qualifications])

  function onChangeServicesFilter(
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const category = z.nativeEnum(ServiceFilter).parse(value)
    const filter = Array.from(new Set(
      event.target.checked
        ? (filters.services ?? []).concat([category])
        : filters.services?.filter(q => q !== value)
    ))
    setFilters({ ...filters, services: filter.length > 0 ? filter : undefined })
  }

  function onChangeDistanceFilter(event: React.ChangeEvent<HTMLInputElement>) {
    console.log(event)
    const distance = distanceFilterSchema.parse(event.currentTarget.value)
    setFilters({ ...filters, distance })
  }

  function onChangeAccreditationsFilter(
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const filter = Array.from(new Set(
      event.target.checked
        ? (filters.accreditations ?? []).concat([value])
        : filters.accreditations?.filter(a => a !== value)
    ))
    setFilters({ ...filters, accreditations: filter.length > 0 ? filter : undefined })
  }

  function onChangeQualificationsFilter(
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const filter = Array.from(new Set(
      event.target.checked
        ? (filters.qualifications ?? []).concat([value])
        : filters.qualifications?.filter(q => q !== value)
    ))
    setFilters({ ...filters, qualifications: filter.length > 0 ? filter : undefined })
  }

  function onChangeBrandsFilter(
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const filter = Array.from(new Set(
      event.target.checked
        ? (filters.brands ?? []).concat([value])
        : filters.brands?.filter(q => q !== value)
    ))
    setFilters({ ...filters, brands: filter.length > 0 ? filter : undefined })
  }

  function onChangeMediaFilter(
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    const media = mediaFilterSchema.safeParse(value)
    if (media.success) {
      const filter = Array.from(new Set(
        event.target.checked
          ? (filters.media ?? []).concat([media.data])
          : filters.media?.filter(q => q !== value)
      ))
      setFilters({ ...filters, media: filter.length > 0 ? filter : undefined })
    }
  }

  return (
    <div className="flex flex-col px-4 pb-4 w-full divide-y divide-solid divide-lightBlue overflow-x-hidden">
      <ServicesFilter title="Installer services" serviceFilters={serviceFilters} onChange={onChangeServicesFilter} value={filters.services} />
      <FilterGroup title="Distance from property">
        <Radio
          options={DISTANCE_FILTER_OPTIONS.map(d => ({ value: String(d), label: formatNumber(d, 'distance') }))}
          value={String(filters.distance)}
          onChange={onChangeDistanceFilter}
        />
      </FilterGroup>
      {accreditationOptions.length > 0
        ? (
          <>
            <CheckboxGroup title="Accreditation" options={accreditationOptions} onChange={onChangeAccreditationsFilter} value={filters.accreditations} />
          </>
          )
        : null}
      <CheckboxGroup title="Training" options={qualificationOptions} onChange={onChangeQualificationsFilter} value={filters.qualifications} />
      <CheckboxGroup title="Profile videos" options={[{ id: 'videos', label: 'Has videos' }]} onChange={onChangeMediaFilter} value={filters.media} />
      <BrandsFilter title="Supported makes" brands={brands} onChange={onChangeBrandsFilter} value={filters.brands} />
    </div>
  )
}

type CheckboxOption = { id: string, label: React.ReactNode }

function CheckboxGroup({
  title,
  options,
  onChange,
  value,
}: {
  title: string
  options: Array<CheckboxOption>
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  value?: string[]
}): React.ReactNode {
  return (
    <FilterGroup title={title}>
      <div
        role="group"
        className="flex flex-col gap-3.5"
      >
        {options.map(option => (
          <div key={option.id} className="flex flex-row gap-2 items-center">
            <input
              id={option.id}
              type="checkbox"
              className="text-brand cursor-pointer h-4 w-4 rounded-sm bg-brand"
              onChange={event => onChange(event, option.id)}
              checked={value != null && value.includes(option.id)}
            />
            <label htmlFor={option.id} className="flex-1 cursor-pointer">
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </FilterGroup>
  )
}

function showBrandByDefault(brand: Brand): boolean {
  return brand.sponsored || brand.popularity > 1
}

function allUnpopular(options: Brand[]): boolean {
  return !options.some(option => !option.sponsored && option.popularity > 1)
}

function BrandsFilter({
  title,
  brands,
  onChange,
  value,
}: {
  title: string
  brands: Brand[]
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  value?: string[]
}): React.ReactNode {
  const [shownBrands, setShownBrands] = React.useState<Brand[]>(allUnpopular(brands) ? brands : brands.filter(showBrandByDefault))
  const [moreShown, setMoreShown] = React.useState<boolean>(false)

  const options: CheckboxOption[] = React.useMemo(() => {
    return shownBrands.map(brand => ({
      id: brand.id,
      label: (
        brand.sponsored === true
          ? (
            <div className="flex flex-row items-center gap-1">
              {brand.name}
              <Chip size="sm">
                Sponsored
              </Chip>
            </div>
            )
          : (
              brand.popularity != null
                ? (
                  <p className="flex flex-row items-center justify-between gap-1">
                    {brand.name}
                    <span className="text-faded text-sm">
                      {brand.popularity}
                    </span>
                  </p>
                  )
                : brand.name
            )
      ),
    }))
  }, [shownBrands])

  React.useEffect(() => {
    setShownBrands(allUnpopular(brands) || moreShown ? brands : brands.filter(showBrandByDefault))
  }, [brands, moreShown])

  return (
    <div>
      <CheckboxGroup title={title} options={options} onChange={onChange} value={value} />
      {(allUnpopular(brands) || (!moreShown && brands.length === options.length))
        ? null
        : (!moreShown
            ? (
              <button
                onClick={() => setMoreShown(true)}
                className="hover:underline text-darkBlue"
              >
                {'Show more \u25BD'}
              </button>
              )
            : (
              <button
                onClick={() => setMoreShown(false)}
                className="hover:underline text-darkBlue"
              >
                {'Show less \u25B3'}
              </button>
              ))}
    </div>
  )
}

function ServicesFilter({
  title,
  serviceFilters,
  onChange,
  value,
}: {
  title: string
  serviceFilters: ServiceFilterEntry[]
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  value?: string[]
}): React.ReactNode {
  const options: CheckboxOption[] = React.useMemo(() => {
    // Iterate the SERVICE_CATEGORY_LABELS to preserve that order
    return Object.entries(SERVICE_FILTER_LABELS).flatMap(([id, label]) => {
      const serviceFilter = serviceFilters.find(f => f.id === id)
      return serviceFilter
        ? [{
            id,
            label: (
              <p className="flex flex-row items-center justify-between gap-1">
                {label}
                <span className="text-faded text-sm">
                  {serviceFilter.popularity}
                </span>
              </p>
            ),
          }]
        : []
    })
  }, [serviceFilters])

  return (
    <CheckboxGroup title={title} options={options} onChange={onChange} value={value} />
  )
}

function Installer({
  installer,
}: {
  installer: InstallerEntry
}): React.ReactNode {
  return (
    <div className="flex flex-col flex-1 gap-2 rounded-none md:rounded-lg md:border-2 md:border-outline bg-white overflow-hidden">
      {installer.sponsored && (
        <div className="text-center bg-lightYellow text-darkYellow text-xs">
          <p>Sponsored</p>
        </div>
      )}
      <div className={`flex flex-col gap-2 ${installer.sponsored ? 'px-4 pb-4' : 'p-4'}`}>
        <div
          className="grid items-start gap-x-4 flex-1 relative"
          style={{
            gridTemplateAreas: `"image name distance"
                  "image headline headline"`,
            gridTemplateColumns: `max-content 1fr max-content`,
            gridTemplateRows: 'auto 1fr',
          }}
        >
          <NextLink
            href={`/installers/${installer.publicId}?address=${installer.addressId}`}
            className="w-full h-full absolute top-0 left-0 z-10"
          />
          <div
            className="w-[84px] md:w-[70px] h-[84px] md:h-[70px] rounded-lg border border-outline overflow-hidden self-center flex justify-center"
            style={{
              gridArea: 'image',
            }}
          >
            {installer.profileImage
              ? (
                <img
                  src={installer.profileImage}
                  alt="installer profile image"
                  style={{ objectFit: 'contain' }}
                />
                )
              : (
                <div className="bg-faded h-full w-full" />
                )}
            {installer.videos > 0 && (
              <div className="flex flex-row items-center gap-0.5 absolute bottom-0 right-0 bg-darkBlue py-0.5 px-1.5 rounded-tl-lg">
                <p className="text-xs text-white">{installer.videos}</p>
                <Icon icon="playCircle" width={14} height={14} />
              </div>
            )}
          </div>
          <h3 className="font-bold" style={{ gridArea: 'name' }}>
            {installer.name}
          </h3>
          <p className="text-sm" style={{ gridArea: 'headline' }}>
            {installer.headline}
          </p>
          <div style={{ gridArea: 'distance' }}>
            <Chip size="sm">
              <Icon icon="place" height={14} width={14} />
              {formatNumber(Math.round(installer.distance), 'distance')}
            </Chip>
          </div>
        </div>
      </div>
    </div>
  )
}

function FilterGroup({
  title,
  children,
}: {
  title: string
  children?: React.ReactNode
}): React.ReactNode {
  return (
    <div className="flex flex-col gap-4 py-4">
      <h3 className="text-xl">{title}</h3>
      {children}
    </div>
  )
}
